import './view.css';
import './proto-inspector';
import { ListenerView } from './listener/listener-view';
import { initTheme } from './theme-manager';
import { enforceBrowserCompatibility } from './core/browser_compat';
import { initI18n } from './i18n';
import { apiUrl } from './api-config';
import { ensureViewerTermsAccepted } from './features/legal/viewer_tc';
import {
  fetchStatusConfig,
  displayAnnouncementIfConfigured,
  renderMaintenanceView,
} from './core/status';
import {
  CLASS_HIDDEN,
  EVENT_DOM_CONTENT_LOADED,
  PARAM_LANG,
  STORAGE_VIEWER_LANGUAGE,
} from './core/strings';
import { isCustomDomain, parseChannelFromUrl, cleanChannelName } from './core/channel_urls';

function setupChannelJoinForm(): void {
  const form = document.getElementById('channel-join-form') as HTMLFormElement | null;
  const input = document.getElementById('channel-join-input') as HTMLInputElement | null;
  if (!form || !input) return;

  form.addEventListener('submit', (e) => {
    e.preventDefault();
    const clean = cleanChannelName(input.value);
    if (!clean) return;
    window.location.href = `/@${encodeURIComponent(clean)}`;
  });
}


function parseLangFromUrl(): string | undefined {
  const params = new URLSearchParams(window.location.search);
  const lang = params.get(PARAM_LANG);
  return lang || undefined;
}

function resolveInitialLang(): string | undefined {
  const fromUrl = parseLangFromUrl();
  if (fromUrl) {
    try {
      if (fromUrl !== 'source') {
        localStorage.setItem(STORAGE_VIEWER_LANGUAGE, fromUrl);
      } else {
        localStorage.removeItem(STORAGE_VIEWER_LANGUAGE);
      }
    } catch {
      // Ignore in restricted environments
    }
    return fromUrl;
  }

  try {
    const saved = localStorage.getItem(STORAGE_VIEWER_LANGUAGE);
    if (saved && saved !== 'source') {
      return saved;
    }
  } catch {
    // Ignore in restricted environments
  }

  return undefined;
}

async function resolveChannel(): Promise<string | null> {
  const fromUrl = parseChannelFromUrl();
  if (fromUrl) return fromUrl;

  try {
    const domain = window.location.hostname.toLowerCase().trim();
    if (!domain || !isCustomDomain(domain)) {
      return null;
    }
    const resp = await fetch(apiUrl(`/api/tenants/by-domain?domain=${encodeURIComponent(domain)}`));
    if (resp.ok) {
      const data = await resp.json();
      if (data.default_channel) {
        return data.default_channel;
      }
      if (Array.isArray(data.channels) && data.channels.length > 0) {
        const primary = data.channels.find((c: any) => c.is_primary);
        return primary ? primary.name : data.channels[0].name;
      }
    }
  } catch (err) {
    console.debug('Could not resolve primary channel by domain:', err);
  }
  return null;
}

let viewInitialized = false;

async function initView(): Promise<void> {
  initI18n();
  initTheme();
  enforceBrowserCompatibility();
  if (viewInitialized) return;

  if (typeof window !== 'undefined' && window.location) {
    const searchParams = new URLSearchParams(window.location.search);
    if (
      searchParams.get('overlay') === 'true' ||
      searchParams.get('overlay') === 'lower3rd' ||
      searchParams.get('mode') === 'overlay'
    ) {
      window.location.replace(`/overlay.html${window.location.search}`);
      return;
    }
  }

  const app = document.getElementById('app') as HTMLElement;
  if (!app) return;

  updateCustomDomainFooter();

  const statusConfig = await fetchStatusConfig();
  if (statusConfig?.maintenance?.enabled) {
    viewInitialized = true;
    renderMaintenanceView(app, statusConfig.maintenance);
    return;
  }

  displayAnnouncementIfConfigured(statusConfig, 'view');

  viewInitialized = true;
  const channelName =
    (typeof document !== 'undefined' ? document.body?.getAttribute('data-ssr-channel') : null) ||
    (await resolveChannel());
  const noChannelView = document.getElementById('no-channel-view');
  const listenerLayout = document.getElementById('listener-layout');

  if (!channelName) {
    if (noChannelView) noChannelView.classList.remove(CLASS_HIDDEN);
    if (listenerLayout) listenerLayout.classList.add(CLASS_HIDDEN);
    setupChannelJoinForm();
    return;
  }

  // Ensure Viewer Terms & Conditions are accepted
  await ensureViewerTermsAccepted();

  if (noChannelView) noChannelView.classList.add(CLASS_HIDDEN);
  if (listenerLayout) listenerLayout.classList.remove(CLASS_HIDDEN);

  const initialLang = resolveInitialLang();
  new ListenerView(app, channelName, initialLang);
}

if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener(EVENT_DOM_CONTENT_LOADED, initView);
  } else {
    initView();
  }
}

export function updateCustomDomainFooter(
  doc: { querySelectorAll: (selector: string) => NodeListOf<Element> | Element[] } = typeof document !== 'undefined' ? document : { querySelectorAll: () => [] },
  isCustom: boolean = isCustomDomain()
): void {
  if (isCustom) {
    doc.querySelectorAll('.custom-domain-powered-by').forEach((el) => el.classList.remove('hidden'));
  }
}

export {
  initView,
  resolveChannel,
  parseChannelFromUrl,
  parseLangFromUrl,
  resolveInitialLang,
  _resetViewInitializedForTesting,
};

function _resetViewInitializedForTesting(): void {
  viewInitialized = false;
}

